/**
 * WelcomeRedirect.tsx
 *
 * This component is used by the /o/<org_slug>/admin/welcome-redirect route,
 * which only appears in the WES welcome Email.
 *
 * The welcome Email contains a link to this page.
 * Based on whether the org already has created a program,
 * this page will redirect the user to the content-curation or the admin-home.
 */
import * as React from 'react';

import URI from 'jsuri';
import { forEach } from 'lodash';
import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import redirect from 'js/lib/coursera.redirect';

import Naptime from 'bundles/naptimejs';
import EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';

type PropsFromNaptime = {
  enterprisePrograms: Array<EnterpriseProgramsV1>;
};

type PropsFromRouter = {
  router: InjectedRouter;
};

export type Props = PropsFromNaptime & PropsFromRouter;

export const FirstTimeRedirect: React.FC<Props> = (props) => {
  const { enterprisePrograms, router } = props;
  const organizationSlug = router.params.thirdPartySlug;
  const query = router.location.query;

  React.useEffect(() => {
    if (enterprisePrograms.length >= 1) {
      router.push({
        pathname: `/o/${organizationSlug}/admin/home`,
        query,
      });
    } else {
      const uri = new URI(`/content-curation/${organizationSlug}/chooseflow`);
      if (query) {
        forEach(query, (v, k) => uri.addQueryParam(k, v));
      }

      redirect.setLocation(uri.toString());
    }
  }, [enterprisePrograms, router, organizationSlug, query]);

  return null;
};

export default compose<Props, {}>(
  connectToRouter((router) => ({ router })),
  Naptime.createContainer<PropsFromNaptime, PropsFromRouter>(({ router }) => {
    const organizationSlug = router.params.thirdPartySlug;
    return {
      enterprisePrograms: EnterpriseProgramsV1.finder('byOrganizationSlug', {
        params: { organizationSlug, includeArchived: false },
        fields: ['metadata', 'contractId', 'launchedAt'],
      }),
    };
  })
)(FirstTimeRedirect);
